import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ref } from 'vue';
import { DocumentNode } from '@apollo/client/core';
import dayjs from 'dayjs';
import { ElMessageBox, ElMessage } from 'element-plus';
import axios from 'axios';
import { makeRequest } from '@/graphs/index';
import { isEmbeddedExperience } from '@/utils/embeddedUtils';
import { thrownGraphErrors } from '@/utils/graphqlResponse';

export default function globalShared(): { [key: string]: any } {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const iOSSafariCheckInterval = 500; // miliseconds
  const iOSSafariMaxTries = 20; // 20 * 0.5 = 10 seconds

  /** The signed-in user's token, or null when signed out (requests then go anonymous). */
  function getAuthToken(): string | null {
    return store.getters['users/me']?.token ?? null;
  }

  /**
   * Confirms a delete, runs `toInvoke`, then toasts success. Return `false` from `toInvoke`
   * when it failed and already showed its own error, so no success toast follows.
   */
  function removeItemGetConfirm(
    toInvoke: () => void | boolean | Promise<void | boolean>,
    options: { title?: string; message?: string; confirmText?: string } = {},
  ) {
    ElMessageBox.confirm(
      options.message || 'Are you sure that you want to delete?',
      options.title || 'Delete',
      {
        type: 'warning',
        customClass: 'c-confirm c-confirm--danger',
        confirmButtonText: options.confirmText || 'Delete',
        cancelButtonText: 'Cancel',
      },
    )
      .then(async () => {
        try {
          if ((await toInvoke()) !== false) ElMessage.info('Successfully Deleted.');
        } catch (error: any) {
          // e.g. "This sticker is the only sticker in a published collection."
          const message = thrownGraphErrors(error)[0]?.message || error?.message;
          ElMessage.error(message || 'Failed to delete');
        }
      })
      .catch(() => {
        ElMessage.info('Delete canceled');
      });
  }

  function getConfirm(message: string, toInvoke: () => void) {
    ElMessageBox.confirm(message, 'Confirm', { type: 'warning', customClass: 'c-confirm' })
      .then(async () => {
        await toInvoke();
        ElMessage.info('Successfully completed.');
      })
      .catch(() => {
        ElMessage.info('Cancelled');
      });
  }

  function showSuccessMessage(message: string) {
    ElMessage.success(message);
  }

  function dateAndTime(str: string): [string, string] {
    const t = dayjs(str);
    return [
      t.format('MM/DD/YYYY'),
      t.format('hh:mm a'),
    ];
  }

  function dobDate(str: string): string {
    const t = dayjs(str);
    return t.format('MM/DD/YYYY');
  }

  async function makeGraphRequest(
    type: string,
    gql: DocumentNode,
    variables: { [key: string]: any },
    token?: string | null,
  ): Promise<any> {
    const context = arguments.length >= 4 ? { token: token ?? null } : undefined;
    const results = await makeRequest(type as 'query' | 'mutation', gql, variables, context);
    if (results && results.errors) {
      if (results.errors[0].message === 'Unauthorized') {
        const isAnonymousRequest = context != null && 'token' in context && !context.token;
        const isPublicEmbed = isEmbeddedExperience({
          path: route.path,
          query: route.query as Record<string, unknown>,
        });

        if (!isAnonymousRequest && !isPublicEmbed) {
          store.dispatch('users/resetMe');
          const currentPath = route.fullPath;
          const isWebApp = currentPath.startsWith('/web');
          const loginPath = isWebApp ? '/auth/web/signin' : '/auth';
          router.push(`${loginPath}?redirect=${encodeURIComponent(currentPath)}`);
        }
      }
    }
    return results;
  }

  function isOrgAdmin(): boolean {
    return store.getters['users/isOrgAdmin'];
  }

  async function getRating(mobileOS: string): Promise<number | null> {
    const baseURL = import.meta.env.VITE_API_BASE_URL;

    try {
      const response = await axios.get(`${baseURL}/api/rating/${mobileOS}`);

      return Number(response.data.rating);
    } catch (_) {
      console.error(_);
      return null;
    }
  }

  function getMobileOperatingSystem() {
    const { userAgent } = navigator;

    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    }

    return 'unknown';
  }

  function isSafari() {
    const { userAgent } = navigator;

    const iOS = !!userAgent.match(/iPad/i) || !!userAgent.match(/iPhone/i);
    const webkit = !!userAgent.match(/WebKit/i);
    const iOSSafari = iOS && webkit && !userAgent.match(/CriOS/i);

    return iOSSafari;
  }

  async function handleSafariPrompt(iteration = 0): Promise<boolean | null> {
    // keep returning promise while the condition is satisfied (document visible and unfocused, iterations below maximum)
    // iOS Safari will natively prompt the user if they want to open the sticker in the proposed App
    // while the safari dialog is active, the document is NOT hidden, and is NOT focused
    // wait until either the document is hidden (user got redirected to app)
    // or until document regained focus (user declined redirect)

    return new Promise((resolve, _reject) => {
      // wait delay between each check
      setTimeout(resolve, iOSSafariCheckInterval);
    }).then(async () => {
      if (iteration > iOSSafariMaxTries) {
        return null;
      }
      if (!document.hidden && !document.hasFocus()) {
        return await handleSafariPrompt(iteration + 1);
      }

      // determines if the redirection was successful (true) or not (false)
      return document.hidden;
    });
  }

  return {
    ref,
    route,
    router,
    store,
    ElMessage,
    ElMessageBox,
    dateAndTime,
    dobDate,
    removeItemGetConfirm,
    getConfirm,
    makeGraphRequest,
    getAuthToken,
    isOrgAdmin,
    getRating,
    getMobileOperatingSystem,
    isSafari,
    handleSafariPrompt,
    showSuccessMessage,
  };
}
